import API from '../config/api';
import React, { useEffect, useState } from 'react';

import axios from 'axios';

import { Container, Card, Badge, Table, Row, Col } from 'react-bootstrap';

import CustomNavbar from '../components/CustomNavbar';

/* Neutral fallback so a missing/dead image never shows a broken icon */
const PLACEHOLDER_IMG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46">
      <rect width="46" height="46" rx="10" fill="#f3ede1"/>
      <text x="23" y="31" font-size="20" text-anchor="middle" fill="#c2410c">📦</text>
    </svg>`
  );

/* Turn seconds into a compact "2m 5s" / "45s" label */
function formatDuration(seconds) {
  const s = Math.round(seconds || 0);

  if (s < 60) {
    return `${s}s`;
  }

  const m = Math.floor(s / 60);
  const rem = s % 60;

  if (m < 60) {
    return rem ? `${m}m ${rem}s` : `${m}m`;
  }

  const h = Math.floor(m / 60);
  const remM = m % 60;

  return remM ? `${h}h ${remM}m` : `${h}h`;
}

/* Short, readable "last viewed" timestamp */
function formatDate(value) {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/* Medal for the top three rows, plain number after */
const RANK_MEDALS = ['🥇', '🥈', '🥉'];

/* Gold / silver / bronze for the podium, neutral after. Drives the rank badge
   and the engagement bar, so the leaderboard reads by colour alone. */
const RANK_TIERS = [
  { key: 'gold', c1: '#b45309', c2: '#fbbf24' },
  { key: 'silver', c1: '#64748b', c2: '#cbd5e1' },
  { key: 'bronze', c1: '#9a3412', c2: '#fdba74' }
];

const tierOf = (index) => RANK_TIERS[index] || null;

function AdminProductTimePage() {
  const [stats, setStats] = useState([]);
  const [userStats, setUserStats] = useState([]);
  const [loading, setLoading] = useState(true);

  const [expandedUser, setExpandedUser] = useState(null);
  const [userProducts, setUserProducts] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const toggleUser = async (visitorId) => {
    if (expandedUser === visitorId) {
      setExpandedUser(null);
      setUserProducts([]);
      return;
    }

    setExpandedUser(visitorId);
    setUserProducts([]);
    setDetailLoading(true);

    try {
      const { data } = await axios.get(
        `${API}/product-views/admin/user/${visitorId}`
      );

      setUserProducts(data);
    } catch (error) {
      console.log(error);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [productRes, userRes] = await Promise.all([
          axios.get(`${API}/product-views/admin/stats`),
          axios.get(`${API}/product-views/admin/user-stats`)
        ]);

        setStats(productRes.data);
        setUserStats(userRes.data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const totalTime = stats.reduce((sum, s) => sum + (s.totalTime || 0), 0);

  const totalViews = stats.reduce((sum, s) => sum + (s.views || 0), 0);

  /* Bars are drawn against the leader, so the top product is always full */
  const peakTime = stats.reduce((max, s) => Math.max(max, s.totalTime || 0), 0);

  const peakUserTime = userStats.reduce(
    (max, u) => Math.max(max, u.totalTime || 0),
    0
  );

  const summaryTiles = [
    {
      icon: '⏱',
      value: formatDuration(totalTime),
      label: 'Total Time',
      c1: '#7c3aed',
      c2: '#a855f7',
      soft: '#f5f3ff'
    },
    {
      icon: '👁',
      value: totalViews.toLocaleString('en-IN'),
      label: 'Product Views',
      c1: '#1d4ed8',
      c2: '#38bdf8',
      soft: '#eff6ff'
    },
    {
      icon: '📦',
      value: stats.length.toLocaleString('en-IN'),
      label: 'Products Viewed',
      c1: '#b45309',
      c2: '#fbbf24',
      soft: '#fffbeb'
    },
    {
      icon: '👤',
      value: userStats.length.toLocaleString('en-IN'),
      label: 'Visitors',
      c1: '#15803d',
      c2: '#22c55e',
      soft: '#f0fdf4'
    }
  ];

  return (
    <>
      <CustomNavbar />

      <div className="apt-wrap">
        <Container>
          {/* Header */}
          <Card className="shadow border-0 mb-4 apt-hero">
            <span className="apt-hero-aurora" aria-hidden="true" />
            <span className="apt-hero-blob apt-hero-blob-1" />
            <span className="apt-hero-blob apt-hero-blob-2" />

            {/* Motes drifting up through the banner */}
            <span className="apt-motes" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <span className="apt-mote" key={i} />
              ))}
            </span>

            <Card.Body className="position-relative p-4 p-md-5">
              <div className="d-flex align-items-center gap-3">
                {/* A real dial rather than an emoji — the hands actually
                    turn, at true 1:12:72 second/minute/hour ratios */}
                <span className="apt-hero-icon" aria-hidden="true">
                  <span className="apt-clock">
                    <i className="apt-tick apt-tick-12" />
                    <i className="apt-tick apt-tick-3" />
                    <i className="apt-tick apt-tick-6" />
                    <i className="apt-tick apt-tick-9" />

                    <span className="apt-hand apt-hand-hour" />
                    <span className="apt-hand apt-hand-min" />
                    <span className="apt-hand apt-hand-sec" />
                    <span className="apt-clock-pin" />
                  </span>
                </span>

                <div className="min-w-0">
                  <h1 className="fw-bold mb-1 apt-hero-title">
                    <span className="apt-hero-title-text">
                      Product Engagement Time
                    </span>
                  </h1>
                  <p className="mb-0 apt-hero-text">
                    How long customers actually spend on each product
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Summary tiles */}
          <Row className="g-3 mb-4">
            {summaryTiles.map((tile, tileIndex) => (
              <Col xs={6} lg={3} key={tile.label}>
                {loading ? (
                  <Card className="border-0 h-100 apt-tile">
                    <Card.Body className="p-3">
                      <span className="apt-skel apt-skel-icon" />
                      <span className="apt-skel apt-skel-value" />
                      <span className="apt-skel apt-skel-label" />
                    </Card.Body>
                  </Card>
                ) : (
                  <Card
                    className="border-0 h-100 apt-tile"
                    style={{
                      '--c1': tile.c1,
                      '--c2': tile.c2,
                      '--soft': tile.soft,
                      '--i': tileIndex
                    }}
                  >
                    <span className="apt-tile-accent" />
                    <Card.Body className="p-3">
                      <span className="apt-tile-icon">{tile.icon}</span>
                      <div className="apt-tile-value">{tile.value}</div>
                      <div className="apt-tile-label">{tile.label}</div>
                    </Card.Body>
                  </Card>
                )}
              </Col>
            ))}
          </Row>

          {/* ===== Products ===== */}
          <div className="apt-section-head">
            <span className="apt-section-icon">📦</span>
            <div>
              <div className="apt-section-title">Time Spent per Product</div>
              <small className="apt-section-hint">
                Ranked by total time across all visitors
              </small>
            </div>
          </div>

          {loading ? (
            <Card className="border-0 shadow-sm apt-table-card">
              <Card.Body className="p-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div className="apt-skel-row" key={i}>
                    <span className="apt-skel apt-skel-thumb" />
                    <div className="flex-grow-1">
                      <span className="apt-skel apt-skel-line" />
                      <span className="apt-skel apt-skel-line short" />
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          ) : stats.length === 0 ? (
            <Card className="border-0 shadow-sm text-center py-5 apt-empty">
              <Card.Body>
                <div style={{ fontSize: '3rem' }}>📊</div>
                <h5 className="fw-bold mt-2">No Engagement Data Yet</h5>
                <p className="text-muted mb-0">
                  Time will appear here once customers browse products.
                </p>
              </Card.Body>
            </Card>
          ) : (
            <Card className="border-0 shadow-sm apt-table-card">
              <Card.Body className="p-0">
                {/* data-label drives the stacked card layout on small screens */}
                <Table hover className="mb-0 align-middle apt-table">
                  <thead>
                    <tr>
                      <th className="apt-rank-col">#</th>
                      <th>Product</th>
                      <th>Category</th>
                      <th>Engagement</th>
                      <th className="text-end">Total Time</th>
                      <th className="text-end">Avg / Visit</th>
                      <th className="text-end">Views</th>
                      <th className="text-end">Visitors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.map((row, index) => {
                      const tier = tierOf(index);

                      return (
                      <tr
                        key={row._id}
                        className={`apt-row${tier ? ` is-${tier.key}` : ''}`}
                        style={{
                          '--c1': tier ? tier.c1 : '#7c3aed',
                          '--c2': tier ? tier.c2 : '#a855f7',
                          /* capped so a long leaderboard doesn't crawl in */
                          '--i': Math.min(index, 12)
                        }}
                      >
                        <td className="apt-rank-col" data-label="Rank">
                          <span className="apt-rank">
                            {RANK_MEDALS[index] || index + 1}
                          </span>
                        </td>

                        <td data-label="Product">
                          <div className="d-flex align-items-center gap-2">
                            <img
                              src={row.image || PLACEHOLDER_IMG}
                              alt={row.productName}
                              className="apt-thumb"
                              onError={(e) => {
                                e.currentTarget.src = PLACEHOLDER_IMG;
                              }}
                            />
                            <span className="fw-semibold apt-name">
                              {row.productName || 'Unknown Product'}
                            </span>
                          </div>
                        </td>

                        <td data-label="Category">
                          <span className="apt-cat">{row.category || '—'}</span>
                        </td>

                        {/* Share of the leader's time, at a glance */}
                        <td data-label="Engagement" className="apt-bar-cell">
                          <div className="apt-bar">
                            <span
                              className="apt-bar-fill"
                              style={{
                                width: `${
                                  peakTime
                                    ? ((row.totalTime || 0) / peakTime) * 100
                                    : 0
                                }%`
                              }}
                            />
                          </div>
                        </td>

                        <td className="text-end apt-time" data-label="Total Time">
                          {formatDuration(row.totalTime)}
                        </td>
                        <td className="text-end" data-label="Avg / Visit">
                          {formatDuration(row.avgTime)}
                        </td>
                        <td className="text-end" data-label="Views">
                          {row.views}
                        </td>
                        <td className="text-end" data-label="Visitors">
                          {row.uniqueVisitors}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {/* ===== Per-customer engagement ===== */}
          {!loading && userStats.length > 0 && (
            <>
              <div className="apt-section-head mt-5">
                <span className="apt-section-icon">👤</span>
                <div>
                  <div className="apt-section-title">
                    Top Customers by Time Spent
                  </div>
                  <small className="apt-section-hint">
                    Tap a row to see what they looked at
                  </small>
                </div>
              </div>

              <Card className="border-0 shadow-sm apt-table-card">
                <Card.Body className="p-0">
                  <Table hover className="mb-0 align-middle apt-table">
                    <thead>
                      <tr>
                        <th className="apt-rank-col">#</th>
                        <th>Customer</th>
                        <th>Email</th>
                        <th>Engagement</th>
                        <th className="text-end">Total Time</th>
                        <th className="text-end">Products</th>
                        <th className="text-end">Visits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userStats.map((row, index) => {
                        const tier = tierOf(index);

                        return (
                        <React.Fragment key={row._id}>
                          <tr
                            className={`apt-row apt-user-row${
                              tier ? ` is-${tier.key}` : ''
                            }${expandedUser === row._id ? ' is-open' : ''}`}
                            style={{
                              '--c1': tier ? tier.c1 : '#1d4ed8',
                              '--c2': tier ? tier.c2 : '#38bdf8',
                              '--i': Math.min(index, 12)
                            }}
                            onClick={() => toggleUser(row._id)}
                          >
                            <td className="apt-rank-col" data-label="Rank">
                              <span className="apt-caret">
                                {expandedUser === row._id ? '▾' : '▸'}
                              </span>
                              <span className="apt-rank">
                                {RANK_MEDALS[index] || index + 1}
                              </span>
                            </td>

                            <td data-label="Customer">
                              <div className="d-flex align-items-center gap-2">
                                <span className="apt-avatar">
                                  {(row.userName || 'G')
                                    .charAt(0)
                                    .toUpperCase()}
                                </span>
                                <span className="fw-semibold">
                                  {row.userName || 'Guest'}
                                </span>
                                {row.isGuest && (
                                  <Badge bg="secondary" className="apt-guest">
                                    Guest
                                  </Badge>
                                )}
                              </div>
                            </td>

                            <td className="text-muted apt-email" data-label="Email">
                              {row.userEmail || '—'}
                            </td>

                            <td data-label="Engagement" className="apt-bar-cell">
                              <div className="apt-bar">
                                <span
                                  className="apt-bar-fill is-user"
                                  style={{
                                    width: `${
                                      peakUserTime
                                        ? ((row.totalTime || 0) /
                                            peakUserTime) *
                                          100
                                        : 0
                                    }%`
                                  }}
                                />
                              </div>
                            </td>

                            <td
                              className="text-end apt-time"
                              data-label="Total Time"
                            >
                              {formatDuration(row.totalTime)}
                            </td>
                            <td className="text-end" data-label="Products">
                              {row.productsViewed}
                            </td>
                            <td className="text-end" data-label="Visits">
                              {row.views}
                            </td>
                          </tr>

                          {expandedUser === row._id && (
                            <tr className="apt-detail-row">
                              <td colSpan={7} className="p-0">
                                {detailLoading ? (
                                  <div className="p-3 text-muted">
                                    Loading products…
                                  </div>
                                ) : userProducts.length === 0 ? (
                                  <div className="p-3 text-muted">
                                    No product views found.
                                  </div>
                                ) : (
                                  <div className="apt-detail">
                                    <div className="apt-detail-head">
                                      🛍 What {row.userName || 'this customer'}{' '}
                                      viewed
                                    </div>

                                    {/* Cards, not a nested table — a table
                                        inside a table row can't reflow. */}
                                    <div className="apt-detail-grid">
                                      {userProducts.map((p, pIndex) => (
                                        <div
                                          className="apt-detail-card"
                                          key={p._id}
                                          style={{
                                            '--i': Math.min(pIndex, 11)
                                          }}
                                        >
                                          <img
                                            src={p.image || PLACEHOLDER_IMG}
                                            alt={p.productName}
                                            className="apt-thumb-sm"
                                            onError={(e) => {
                                              e.currentTarget.src =
                                                PLACEHOLDER_IMG;
                                            }}
                                          />

                                          <div className="min-w-0 flex-grow-1">
                                            <div className="apt-detail-name">
                                              {p.productName || 'Unknown'}
                                            </div>
                                            <div className="apt-detail-meta">
                                              {p.category || '—'} ·{' '}
                                              {p.views} view
                                              {p.views === 1 ? '' : 's'}
                                            </div>
                                            <div className="apt-detail-when">
                                              {formatDate(p.lastViewed)}
                                            </div>
                                          </div>

                                          <div className="apt-detail-time">
                                            {formatDuration(p.totalTime)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                        );
                      })}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </>
          )}
        </Container>
      </div>

      <style>{adminProductTimeStyles}</style>
    </>
  );
}

const adminProductTimeStyles = `
  .apt-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 40px) 0;
  }
  .min-w-0 { min-width: 0; }

  /* ===== Hero ===== */
  .apt-hero {
    position: relative;
    border-radius: 22px;
    overflow: hidden;
    isolation: isolate;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #3b0764 28%, #7c2d12 60%, #ea580c 86%, #f59e0b);
    background-size: 220% 220%;
    border: 1px solid rgba(253, 230, 138, 0.25) !important;
    animation: aptDrift 16s ease-in-out infinite;
  }
  @keyframes aptDrift {
    0%, 100% { background-position: 0% 50%; }
    50%      { background-position: 100% 50%; }
  }
  /* Gold threads along both edges, running opposite ways */
  .apt-hero::before,
  .apt-hero::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 2px;
    z-index: 3;
    background: linear-gradient(90deg, transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 220% 100%;
    animation: aptThread 3.6s linear infinite;
  }
  .apt-hero::before { top: 0; }
  .apt-hero::after { bottom: 0; animation-direction: reverse; }
  @keyframes aptThread {
    to { background-position: 220% 0; }
  }

  .apt-hero-aurora {
    position: absolute;
    inset: -40%;
    z-index: 0;
    pointer-events: none;
    background:
      radial-gradient(36% 44% at 24% 30%, rgba(251,191,36,0.42), transparent 62%),
      radial-gradient(32% 40% at 78% 26%, rgba(168,85,247,0.34), transparent 62%),
      radial-gradient(40% 46% at 58% 84%, rgba(56,189,248,0.30), transparent 62%);
    filter: blur(30px);
    animation: aptAurora 22s ease-in-out infinite alternate;
    will-change: transform;
  }
  @keyframes aptAurora {
    0%   { transform: translate3d(-4%, -3%, 0) rotate(0deg)  scale(1.05); }
    50%  { transform: translate3d(5%, 4%, 0)   rotate(8deg)  scale(1.18); }
    100% { transform: translate3d(-3%, 5%, 0)  rotate(-6deg) scale(1.1); }
  }

  .apt-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    pointer-events: none;
    z-index: 0;
  }
  .apt-hero-blob-1 {
    width: 200px; height: 200px;
    background: #fbbf24;
    top: -90px; left: 18%;
    opacity: 0.45;
  }
  .apt-hero-blob-2 {
    width: 180px; height: 180px;
    background: #38bdf8;
    bottom: -90px; right: 8%;
    opacity: 0.3;
  }

  .apt-motes { position: absolute; inset: 0; z-index: 1; pointer-events: none; overflow: hidden; }
  .apt-mote {
    position: absolute;
    bottom: -12px;
    width: 6px; height: 6px;
    border-radius: 50%;
    background: rgba(253, 230, 138, 0.9);
    box-shadow: 0 0 12px rgba(251, 191, 36, 0.9);
    opacity: 0;
    animation: aptMote 12s linear infinite;
  }
  .apt-mote:nth-child(1) { left: 7%;  animation-delay: 0s; }
  .apt-mote:nth-child(2) { left: 20%; animation-delay: 4.3s; width: 4px; height: 4px; }
  .apt-mote:nth-child(3) { left: 33%; animation-delay: 1.7s; }
  .apt-mote:nth-child(4) { left: 46%; animation-delay: 7.2s; width: 5px; height: 5px; }
  .apt-mote:nth-child(5) { left: 59%; animation-delay: 2.8s; }
  .apt-mote:nth-child(6) { left: 72%; animation-delay: 9.2s; width: 4px; height: 4px; }
  .apt-mote:nth-child(7) { left: 85%; animation-delay: 5.6s; }
  .apt-mote:nth-child(8) { left: 95%; animation-delay: 3.4s; width: 5px; height: 5px; }
  @keyframes aptMote {
    0%   { opacity: 0; transform: translateY(0) translateX(0) scale(0.6); }
    12%  { opacity: 1; }
    80%  { opacity: 0.6; }
    100% { opacity: 0; transform: translateY(-200px) translateX(22px) scale(1.1); }
  }

  .apt-hero .card-body { position: relative; z-index: 2; }

  /* ===== Clock dial =====
     A brass case around a cream face. Everything inside is sized in % of the
     face, so the small-screen override only has to change the outer box. */
  .apt-hero-icon {
    position: relative;
    width: 62px;
    height: 62px;
    flex-shrink: 0;
    border-radius: 50%;
    padding: 4px;
    background: linear-gradient(145deg, #fde68a, #b45309 55%, #fbbf24);
    box-shadow:
      0 0 0 1px rgba(253, 230, 138, 0.55),
      0 8px 22px rgba(0, 0, 0, 0.35),
      inset 0 1px 2px rgba(255, 255, 255, 0.6);
  }
  .apt-clock {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background:
      radial-gradient(circle at 34% 28%, #ffffff, #fff7ed 45%, #fde9c8 100%);
    box-shadow: inset 0 2px 5px rgba(124, 45, 18, 0.28);
    overflow: hidden;
  }

  /* Hour markers at 12 / 3 / 6 / 9 */
  .apt-tick {
    position: absolute;
    width: 2px;
    height: 13%;
    border-radius: 2px;
    background: #b45309;
    opacity: 0.75;
  }
  .apt-tick-12 { top: 7%;  left: 50%; margin-left: -1px; }
  .apt-tick-6  { bottom: 7%; left: 50%; margin-left: -1px; }
  .apt-tick-3  { right: 7%; top: 50%; margin-top: -1px; width: 13%; height: 2px; }
  .apt-tick-9  { left: 7%;  top: 50%; margin-top: -1px; width: 13%; height: 2px; }

  /* Hands pivot on the centre pin. translateX stays inside the keyframes,
     otherwise the animation would drop it and the hands would sit off-axis. */
  .apt-hand {
    position: absolute;
    left: 50%;
    bottom: 50%;
    transform-origin: 50% 100%;
    /* base transform matters: if the animation is switched off the hands must
       still sit on the pin rather than half a hand-width to the right */
    transform: translateX(-50%);
    border-radius: 3px;
    background: #7c2d12;
  }
  .apt-hand-hour {
    width: 3px;
    height: 26%;
    margin-left: -1.5px;
    animation: aptHand 432s linear infinite;
  }
  .apt-hand-min {
    width: 2.5px;
    height: 36%;
    margin-left: -1.25px;
    animation: aptHand 72s linear infinite;
  }
  /* The one you actually see move */
  .apt-hand-sec {
    width: 1.5px;
    height: 42%;
    margin-left: -0.75px;
    background: #dc2626;
    animation: aptHand 6s linear infinite;
  }
  @keyframes aptHand {
    from { transform: translateX(-50%) rotate(0deg); }
    to   { transform: translateX(-50%) rotate(360deg); }
  }
  .apt-clock-pin {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 5px;
    height: 5px;
    margin: -2.5px 0 0 -2.5px;
    border-radius: 50%;
    background: #dc2626;
    box-shadow: 0 0 0 1.5px #fff7ed;
  }

  .apt-hero-title { font-size: clamp(1.4rem, 3.6vw, 2.1rem); }
  .apt-hero-title-text {
    background: linear-gradient(100deg,
      #ffffff 0%, #ffffff 32%, #fde68a 46%, #fbbf24 52%, #fde68a 58%, #ffffff 74%, #ffffff 100%);
    background-size: 250% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: aptTitleShine 5.5s ease-in-out infinite;
  }
  @keyframes aptTitleShine {
    0%, 100% { background-position: 130% 0; }
    50%      { background-position: -30% 0; }
  }
  .apt-hero-text { opacity: 0.88; font-size: 0.92rem; }

  /* ===== Summary tiles ===== */
  .apt-tile {
    position: relative;
    overflow: hidden;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 6px 18px rgba(124, 45, 18, 0.07);
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    animation: aptTileIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
    animation-delay: calc(var(--i, 0) * 80ms);
  }
  @keyframes aptTileIn {
    from { opacity: 0; transform: translateY(16px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .apt-tile::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--soft);
    opacity: 0.55;
    pointer-events: none;
  }
  .apt-tile:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 30px rgba(124, 45, 18, 0.16);
  }
  /* Light travels down the accent bar */
  .apt-tile-accent {
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 4px;
    z-index: 2;
    background: linear-gradient(180deg, var(--c1), var(--c2), var(--c1));
    background-size: 100% 220%;
    animation: aptAccent 3.2s linear infinite;
  }
  @keyframes aptAccent {
    to { background-position: 0 220%; }
  }
  .apt-tile .card-body { position: relative; z-index: 1; }
  .apt-tile-icon {
    display: inline-block;
    font-size: 1.4rem;
    line-height: 1;
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .apt-tile:hover .apt-tile-icon { transform: scale(1.2) rotate(-8deg); }
  .apt-tile-value {
    margin-top: 8px;
    font-weight: 800;
    font-size: clamp(1.05rem, 2.2vw, 1.5rem);
    line-height: 1.15;
    background: linear-gradient(120deg, var(--c1), var(--c2));
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .apt-tile-label {
    font-size: 0.76rem;
    font-weight: 600;
    color: #78716c;
  }

  /* ===== Section headings ===== */
  .apt-section-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
  }
  .apt-section-icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
    border-radius: 13px;
    background: linear-gradient(135deg, #fff7ed, #ffedd5);
    border: 1px solid #fed7aa;
  }
  .apt-section-title {
    font-weight: 800;
    font-size: 1.05rem;
    color: #7c2d12;
    line-height: 1.2;
  }
  .apt-section-hint { color: #a8a29e; font-size: 0.76rem; }

  /* ===== Tables ===== */
  .apt-table-card {
    border-radius: 18px;
    overflow: hidden;
  }
  .apt-table thead th {
    background: #faf7f2;
    border-bottom: 1px solid #f1e4d0;
    font-size: 0.68rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #a8a29e;
    padding: 13px 16px;
    white-space: nowrap;
  }
  .apt-table td {
    padding: 12px 16px;
    border-color: #f5f1ea;
  }
  /* Rows deal themselves in, staggered by --i */
  .apt-row {
    animation: aptRowIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) backwards;
    animation-delay: calc(var(--i, 0) * 45ms);
  }
  @keyframes aptRowIn {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  /* The podium keeps a faint tint of its metal */
  .apt-row.is-gold   > td { background: linear-gradient(90deg, rgba(251,191,36,0.13), transparent 45%); }
  .apt-row.is-silver > td { background: linear-gradient(90deg, rgba(148,163,184,0.14), transparent 45%); }
  .apt-row.is-bronze > td { background: linear-gradient(90deg, rgba(234,88,12,0.10), transparent 45%); }

  .apt-rank-col { width: 74px; }
  .apt-rank {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    font-weight: 800;
    font-size: 1rem;
    color: #78716c;
  }
  /* Only the winner's medal shines */
  .apt-row.is-gold .apt-rank {
    animation: aptMedal 2.6s ease-in-out infinite;
  }
  @keyframes aptMedal {
    0%, 100% { transform: scale(1);    filter: drop-shadow(0 0 0 rgba(251,191,36,0)); }
    50%      { transform: scale(1.18); filter: drop-shadow(0 0 8px rgba(251,191,36,0.95)); }
  }
  .apt-thumb {
    width: 44px;
    height: 44px;
    object-fit: cover;
    border-radius: 10px;
    flex-shrink: 0;
    background: #faf7f2;
    border: 1px solid #f1e4d0;
  }
  .apt-name {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
    color: #1c1917;
  }
  .apt-cat {
    font-size: 0.72rem;
    font-weight: 700;
    color: #57534e;
    background: #f5f5f4;
    border-radius: 20px;
    padding: 3px 10px;
    white-space: nowrap;
  }
  .apt-time {
    font-weight: 800;
    color: #7c3aed;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .apt-email {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .apt-avatar {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #fff;
    font-weight: 800;
    font-size: 0.82rem;
    background: linear-gradient(135deg, #7c3aed, #a855f7);
  }
  .apt-guest { font-size: 0.6rem; }

  /* Engagement bar */
  .apt-bar-cell { min-width: 130px; }
  .apt-bar {
    height: 8px;
    border-radius: 8px;
    background: rgba(15, 23, 42, 0.08);
    overflow: hidden;
  }
  /* The bar wears the row's own rank colour */
  .apt-bar-fill {
    position: relative;
    display: block;
    height: 100%;
    border-radius: 8px;
    background: linear-gradient(90deg, var(--c2, #a855f7), var(--c1, #7c3aed));
    transition: width 0.7s cubic-bezier(0.22, 1, 0.36, 1);
    animation: aptBarGrow 0.8s cubic-bezier(0.22, 1, 0.36, 1) backwards;
    animation-delay: calc(var(--i, 0) * 45ms);
  }
  @keyframes aptBarGrow {
    from { transform: scaleX(0); }
    to   { transform: scaleX(1); }
  }
  .apt-bar-fill { transform-origin: left center; }
  /* Light runs along the leader's bar */
  .apt-row.is-gold .apt-bar-fill::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
    background-size: 220% 100%;
    animation: aptBarSheen 2.4s linear infinite;
  }
  @keyframes aptBarSheen {
    to { background-position: 220% 0; }
  }

  .apt-user-row { cursor: pointer; }
  .apt-user-row:hover > td { background: #fffaf3; }
  .apt-user-row.is-open > td { background: #fff7ed; }
  .apt-caret {
    display: inline-block;
    width: 16px;
    color: #ea580c;
    font-weight: 700;
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .apt-user-row.is-open .apt-caret { transform: rotate(0deg) scale(1.2); }

  /* Expanded detail */
  .apt-detail-row > td { background: #fdfcfa; }
  .apt-detail {
    border-left: 4px solid #ea580c;
    padding: 14px 16px;
  }
  .apt-detail-head {
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #a8a29e;
    margin-bottom: 10px;
  }
  .apt-detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
    gap: 10px;
  }
  .apt-detail-card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid #f1e4d0;
    transition: transform 0.25s ease, box-shadow 0.25s ease,
      border-color 0.25s ease;
    animation: aptDetailIn 0.4s cubic-bezier(0.22, 1, 0.36, 1) backwards;
    animation-delay: calc(var(--i, 0) * 45ms);
  }
  @keyframes aptDetailIn {
    from { opacity: 0; transform: translateX(-12px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .apt-detail-card:hover {
    transform: translateY(-3px);
    border-color: #fdba74;
    box-shadow: 0 10px 22px rgba(194, 65, 12, 0.14);
  }
  .apt-thumb-sm {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 9px;
    flex-shrink: 0;
    background: #faf7f2;
    border: 1px solid #f1e4d0;
  }
  .apt-detail-name {
    font-weight: 700;
    font-size: 0.86rem;
    color: #1c1917;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .apt-detail-meta, .apt-detail-when {
    font-size: 0.72rem;
    color: #a8a29e;
  }
  .apt-detail-time {
    font-weight: 800;
    font-size: 0.88rem;
    color: #7c3aed;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .apt-empty { border-radius: 18px; }

  /* Skeletons */
  .apt-skel {
    display: block;
    background: linear-gradient(90deg, #f1ece4 25%, #faf7f2 37%, #f1ece4 63%);
    background-size: 400% 100%;
    animation: aptShimmer 1.4s ease infinite;
    border-radius: 8px;
  }
  .apt-skel-icon  { width: 32px; height: 32px; border-radius: 10px; }
  .apt-skel-value { width: 65%; height: 20px; margin-top: 10px; }
  .apt-skel-label { width: 45%; height: 10px; margin-top: 8px; }
  .apt-skel-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 6px;
  }
  .apt-skel-thumb { width: 44px; height: 44px; border-radius: 10px; flex-shrink: 0; }
  .apt-skel-line  { height: 11px; width: 60%; margin-bottom: 8px; }
  .apt-skel-line.short { width: 35%; margin-bottom: 0; }
  @keyframes aptShimmer {
    0%   { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  /* ===================================================================
     Small screens: the tables become stacked cards. Seven columns can't
     be read by side-scrolling on a phone, so each row turns into a card
     and every cell shows its own label.
     =================================================================== */
  @media (max-width: 991.98px) {
    .apt-table, .apt-table tbody, .apt-table tr, .apt-table td {
      display: block;
      width: 100%;
    }
    .apt-table thead { display: none; }

    .apt-table tbody tr {
      position: relative;
      margin: 12px;
      padding: 6px 4px;
      border: 1px solid #f1e4d0;
      border-radius: 14px;
      background: #fff;
      box-shadow: 0 4px 12px rgba(124, 45, 18, 0.06);
    }
    .apt-table td {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 7px 12px;
      border: none;
      text-align: left !important;
    }
    /* the label that replaces the missing column header */
    .apt-table td::before {
      content: attr(data-label);
      flex-shrink: 0;
      font-size: 0.66rem;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #a8a29e;
    }
    /* rank + product read as the card's header */
    .apt-table td[data-label="Rank"],
    .apt-table td[data-label="Product"],
    .apt-table td[data-label="Customer"] {
      justify-content: flex-start;
    }
    .apt-table td[data-label="Rank"]::before,
    .apt-table td[data-label="Product"]::before,
    .apt-table td[data-label="Customer"]::before {
      display: none;
    }
    .apt-table td[data-label="Product"],
    .apt-table td[data-label="Customer"] {
      padding-bottom: 10px;
      border-bottom: 1px dashed #f1e4d0;
    }
    .apt-rank-col { width: auto; }
    .apt-name, .apt-email { max-width: none; }
    .apt-bar-cell { min-width: 0; }
    .apt-bar { flex: 1 1 auto; }

    /* the expanded panel spans its own card */
    .apt-detail-row > td { padding: 0 !important; }
    .apt-detail-row { border: none !important; box-shadow: none !important; margin: 0 12px 12px !important; }
    .apt-detail-grid { grid-template-columns: 1fr; }
  }

  @media (max-width: 575.98px) {
    .apt-hero-icon { width: 50px; height: 50px; padding: 3px; }
    .apt-section-icon { width: 34px; height: 34px; font-size: 1rem; }
    .apt-section-title { font-size: 0.95rem; }
    .apt-tile-value { font-size: 1.05rem; }
  }

  @media (prefers-reduced-motion: reduce) {
    .apt-hero,
    .apt-hero::before,
    .apt-hero::after,
    .apt-hero-aurora,
    .apt-mote,
    .apt-hand,
    .apt-hero-title-text,
    .apt-tile,
    .apt-tile-accent,
    .apt-row,
    .apt-row.is-gold .apt-rank,
    .apt-row.is-gold .apt-bar-fill::after,
    .apt-bar-fill,
    .apt-detail-card,
    .apt-skel { animation: none !important; transition: none; }
    .apt-motes { display: none; }
    /* Park the hands at a plausible time instead of stacking them at 12 */
    .apt-hand-hour { transform: translateX(-50%) rotate(300deg); }
    .apt-hand-min  { transform: translateX(-50%) rotate(60deg); }
    .apt-hand-sec  { transform: translateX(-50%) rotate(210deg); }
    .apt-hero-title-text {
      -webkit-text-fill-color: #fff;
      color: #fff;
    }
  }
`;

export default AdminProductTimePage;
